import * as React from 'react';
import { AutoSizer } from 'react-virtualized';
import styled from 'styled-components';
import { ISizer } from '../../../data/types';
import { MainGrid } from '../../layout/MainGrid';
import { ScrollableArea } from '../../layout/ScrollableArea';

interface IProps {
  children: React.ReactNode;
}

export const ReportsGrid = (props: IProps): React.ReactElement => {
  return <MainGrid direction='horizontal'>{props.children}</MainGrid>;
};

interface ISettingsContainerProps {
  children: React.ReactNode;
}

export const SettingsContainer = (
  props: ISettingsContainerProps
): React.ReactElement => {
  return (
    <ScrollableArea>
      <div style={{ padding: '5px' }}>
        <AutoSizer>
          {(size: ISizer): React.ReactElement => {
            const { width, height } = size;
            return <div style={{ height, width }}>{props.children}</div>;
          }}
        </AutoSizer>
      </div>
    </ScrollableArea>
  );
};

interface IReportContainerProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
}

export const ReportContainer = (
  props: IReportContainerProps
): React.ReactElement => {
  return (
    <ScrollableArea style={{ background: 'var(--color-list-background)' }}>
      <div style={{ width: '100%', height: '100%', ...props.style }}>
        <AutoSizer>
          {(size: ISizer): React.ReactElement => {
            const { width, height } = size;
            return <div style={{ height, width }}>{props.children}</div>;
          }}
        </AutoSizer>
      </div>
    </ScrollableArea>
  );
};

export const ConfigSection = styled.div`
  margin-top: 10px;
  padding: 10px;
`;

interface IConfigSectionHeaderProps {
  backgroundColor?: string;
  onClear?: () => void;
  children: React.ReactElement;
  style?: React.CSSProperties;
}
export const ConfigSectionHeader = (
  props: IConfigSectionHeaderProps
): React.ReactElement => {
  return (
    <ConfigSectionHeaderContainer
      style={{ backgroundColor: props.backgroundColor, ...props.style }}
    >
      {props.children}
      {typeof props.onClear === 'function' ? (
        <Clear onClear={props.onClear} />
      ) : null}
    </ConfigSectionHeaderContainer>
  );
};

const Clear = (props: { onClear: () => void }): React.ReactElement => {
  return <ClearLink onClick={props.onClear}>Clear</ClearLink>;
};

const ClearLink = styled.a`
  margin-left: auto;
  margin-right: 5px;
  text-decoration: underline;
  cursor: pointer;
  color: #fff;
`;

export const ConfigSectionHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 5px;
`;

export const ConfigSectionTitle = styled.div``;

export const TooltipWrapper = styled.div`
  margin: 0;
  padding: 10px;
  background-color: var(--color-tooltip);
  border-radius: 10px;
  white-space: nowrap;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23);
`;

export const highlightSearchMatch = (
  text: string,
  matchText: string
): React.ReactElement => {
  const matcher = new RegExp(`/${matchText}\{0,1}/`, 'gi');
  const replacements = text.split(matchText);

  const replaced = replacements.reduce(
    (
      acc: Array<string | React.ReactElement>,
      curr: string,
      currentIndex: number
    ): Array<string | React.ReactElement> => {
      if (currentIndex % 2 === 0 && currentIndex !== 0) {
        acc.push(<HighlightedSpan>{matchText}</HighlightedSpan>);
        acc.push(curr);
      } else {
        acc.push(curr);
      }
      return acc;
    },
    []
  );
  return <span>{replaced}</span>;
};

const HighlightedSpan = styled.span`
  background-color: var(--color-material-500-light-green);
  color: var(--color-dark-text);
`;
